import type { PrivateKey } from 'starkbank-ecdsa';
import { PrivateKey as PrivateKeyClass } from 'starkbank-ecdsa';
import { secp256k1 } from 'starkbank-ecdsa/ellipticcurve/curve';
import BigInt from 'big-integer';
import { DebugLogger } from './debug';

const DB_NAME = 'VeltizSessionDB';
const DB_VERSION = 1;
const STORE_NAME = 'sessionKeys';

interface StoredSessionKey {
  sessionId: string;
  privateKeyData: {
    secret: string; // Private key parameter as hex string
  };
  expiresAt: string;
  userId: string;
}

export class SecureSessionStorage {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'sessionId' });
          store.createIndex('userId', 'userId', { unique: false });
          store.createIndex('expiresAt', 'expiresAt', { unique: false });
        }
      };
    });
  }

  async storeSessionKey(
    sessionId: string,
    privateKey: PrivateKey,
    expiresAt: string,
    userId: string
  ): Promise<void> {
    DebugLogger.session('Storing session key', { 
      sessionId, 
      userId, 
      expiresAt,
      keyLength: (privateKey as any).secret.toString(16).length 
    });
    
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      // Convert private key to storable format
      const privateKeyData = {
        secret: (privateKey as any).secret.toString(16)
      };

      const data: StoredSessionKey = {
        sessionId,
        privateKeyData,
        expiresAt,
        userId
      };

      const request = store.put(data);

      request.onsuccess = () => {
        DebugLogger.session('Session key stored successfully', { sessionId, userId });
        resolve();
      };
      request.onerror = () => {
        DebugLogger.error('Failed to store session key', { sessionId, userId });
        reject(new Error('Failed to store session key'));
      };
    });
  }

  async getSessionKey(sessionId: string): Promise<PrivateKey | null> {
    DebugLogger.session('Retrieving session key', { sessionId });
    
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(sessionId);

      request.onsuccess = () => {
        const result = request.result as StoredSessionKey | undefined;
        
        if (!result) {
          DebugLogger.session('Session key not found in storage', { sessionId });
          resolve(null);
          return;
        }

        // Check if session has expired
        if (new Date(result.expiresAt) < new Date()) {
          DebugLogger.session('Session key expired, removing from storage', { 
            sessionId, 
            expiresAt: result.expiresAt,
            currentTime: new Date().toISOString()
          });
          this.removeSessionKey(sessionId);
          resolve(null);
          return;
        }

        try {
          // Reconstruct private key from stored data
          const privateKey = this.reconstructPrivateKey(result.privateKeyData);
          DebugLogger.session('Session key retrieved successfully', { 
            sessionId, 
            userId: result.userId,
            expiresAt: result.expiresAt
          });
          resolve(privateKey);
        } catch (error) {
          DebugLogger.error('Failed to reconstruct private key', { sessionId, error });
          reject(new Error('Failed to reconstruct private key'));
        }
      };

      request.onerror = () => {
        DebugLogger.error('Failed to retrieve session key', { sessionId });
        reject(new Error('Failed to retrieve session key'));
      };
    });
  }

  async getUserId(sessionId: string): Promise<string | null> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(sessionId);

      request.onsuccess = () => {
        const result = request.result as StoredSessionKey | undefined;
        resolve(result?.userId ?? null);
      };
      request.onerror = () => reject(new Error('Failed to retrieve user ID'));
    });
  }

  async removeSessionKey(sessionId: string): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(sessionId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to remove session key'));
    });
  }

  async clearExpiredSessions(): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('expiresAt');
      const request = index.openCursor();

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const data = cursor.value as StoredSessionKey;
          if (new Date(data.expiresAt) < new Date()) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => reject(new Error('Failed to clear expired sessions'));
    });
  }

  private reconstructPrivateKey(privateKeyData: StoredSessionKey['privateKeyData']): PrivateKey {
    // Reconstruct private key using the built-in secp256k1 curve
    const secret = BigInt(privateKeyData.secret, 16);
    return new PrivateKeyClass(secp256k1, secret as any);
  }
}

// Export a singleton instance
export const secureSessionStorage = new SecureSessionStorage();
